import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type GeoRestrictedToken,
  getRestrictedCountriesForToken,
  isTokenRestrictedInCountry,
} from '@/lib/geo/restrictedIssuers';

export const geoCheckQueryKey = () => ['geo-check'] as const;

/** Fetches the user's country and shares it through the query cache. */
export const useGeoCheck = () =>
  useQuery({
    queryKey: geoCheckQueryKey(),
    queryFn: async () => {
      const res = await fetch('/api/geo-check');
      if (!res.ok) throw new Error(`Geo check failed: ${res.statusText}`);
      const data = (await res.json()) as { country: string };
      return data;
    },
    refetchOnReconnect: false,
  });

export type TradeGeoStatus = 'geo-checking' | 'geo-allowed' | 'geo-blocked';

export const useTradeGeoStatus = (
  buyToken?: GeoRestrictedToken,
  sellToken?: GeoRestrictedToken,
): TradeGeoStatus => {
  const { data, isSuccess } = useGeoCheck();

  return useMemo(() => {
    const requiresGeoCheck = [buyToken, sellToken].some(
      (token) => getRestrictedCountriesForToken(token).length > 0,
    );
    if (!requiresGeoCheck) return 'geo-allowed';
    if (!isSuccess || !data) return 'geo-checking';

    return [buyToken, sellToken].some((token) => isTokenRestrictedInCountry(token, data.country))
      ? 'geo-blocked'
      : 'geo-allowed';
  }, [buyToken, data, isSuccess, sellToken]);
};
